import {
  CurrentStockResponse,
  Product,
  QuantityUnit,
  QuantityUnitConversion,
  RecipeIngredient,
} from "../utils/grocyApi.ts";

export class UnitConversionService {
  private aggregatedStockCache = new Map<number, number>();

  constructor(
    private conversions: QuantityUnitConversion[],
    private quantityUnits: QuantityUnit[],
    private allStock: CurrentStockResponse[],
    private allProducts: Product[],
  ) {
    // Pre-calculate aggregated stock for all products
    this.buildStockAggregations();
  }

  // Pre-calculate aggregated stock for all products during construction
  private buildStockAggregations(): void {
    this.aggregatedStockCache.clear();

    for (const product of this.allProducts) {
      this.calculateAggregatedStock(product.id);
    }
  }

  // Calculate aggregated stock for a product (includes child products)
  private calculateAggregatedStock(productId: number): number {
    if (this.aggregatedStockCache.has(productId)) {
      return this.aggregatedStockCache.get(productId)!;
    }

    const product = this.allProducts.find((p) => p.id === productId);
    if (!product) {
      this.aggregatedStockCache.set(productId, 0);
      return 0;
    }

    // Get direct stock for this product
    const directStock =
      this.allStock.find((s) => s.product_id === productId)?.amount || 0;

    // Find all child products (products that have this product as parent)
    const childProducts = this.allProducts.filter((p) =>
      p.parent_product_id === productId
    );

    // Sum up child stock (converted to parent's units)
    let childStockSum = 0;
    for (const child of childProducts) {
      const childStock = this.allStock.find((s) =>
        s.product_id === child.id
      )?.amount || 0;

      if (childStock > 0) {
        // Convert child stock to parent's stock units
        const convertedChildStock = this.convertChildStockAmount(
          childStock,
          child.qu_id_stock,
          product.qu_id_stock,
          child.id,
          product.id,
        );

        if (convertedChildStock !== null) {
          childStockSum += convertedChildStock;
        } else {
          // If conversion fails, log warning but don't include in sum
          console.warn(
            `Cannot convert child product ${child.id} stock to parent product ${productId} units`,
          );
        }
      }
    }

    const totalStock = directStock + childStockSum;
    this.aggregatedStockCache.set(productId, totalStock);
    return totalStock;
  }

  // Get aggregated stock for a product (public method)
  getAggregatedStock(productId: number): number {
    return this.aggregatedStockCache.get(productId) || 0;
  }

  // Core conversion logic with chained conversion support
  convertAmount(
    amount: number,
    fromUnitId: number,
    toUnitId: number,
    productId: number,
  ): number | null {
    if (fromUnitId === toUnitId) return amount;

    // Try direct conversion first (fastest)
    const directResult = this.findDirectConversion(
      amount,
      fromUnitId,
      toUnitId,
      productId,
    );
    if (directResult !== null) return directResult;

    // Try chained conversion (more comprehensive)
    return this.findChainedConversion(amount, fromUnitId, toUnitId, productId);
  }

  private convertChildStockAmount(
    amount: number,
    fromUnitId: number,
    toUnitId: number,
    childProductId: number,
    parentProductId: number,
  ): number | null {
    return this.convertAmountWithProductFallbacks(
      amount,
      fromUnitId,
      toUnitId,
      [childProductId, parentProductId],
    );
  }

  private convertAmountWithProductFallbacks(
    amount: number,
    fromUnitId: number,
    toUnitId: number,
    productIds: number[],
  ): number | null {
    if (fromUnitId === toUnitId) return amount;

    for (const productId of productIds) {
      const directResult = this.findDirectConversion(
        amount,
        fromUnitId,
        toUnitId,
        productId,
      );
      if (directResult !== null) return directResult;
    }

    for (const productId of productIds) {
      const chainedResult = this.findChainedConversion(
        amount,
        fromUnitId,
        toUnitId,
        productId,
      );
      if (chainedResult !== null) return chainedResult;
    }

    return null;
  }

  // Recipe ingredient display conversion
  getIngredientDisplayAmount(
    ingredient: RecipeIngredient,
    product: Product,
  ): { amount: number; unit: QuantityUnit } {
    const unit = this.quantityUnits.find((u) => u.id === ingredient.qu_id)!;

    // If only_check_single_unit_in_stock is 1, use original recipe amount and unit
    if (ingredient.only_check_single_unit_in_stock === 1) {
      return {
        amount: ingredient.amount,
        unit,
      };
    }

    // Convert from stock units to recipe units for display
    const convertedAmount = this.convertAmount(
      ingredient.amount,
      product.qu_id_stock,
      ingredient.qu_id,
      product.id,
    );

    return {
      amount: convertedAmount || ingredient.amount,
      unit,
    };
  }

  // Recipe ingredient stock comparison
  getIngredientStockComparison(
    ingredient: RecipeIngredient,
    product: Product,
  ): { isAvailable: boolean; convertedRequired: number } {
    // Get aggregated stock (includes child products)
    const currentStock = this.getAggregatedStock(product.id);

    // If only_check_single_unit_in_stock is 1, any amount > 0 is considered in stock
    if (ingredient.only_check_single_unit_in_stock === 1) {
      return {
        isAvailable: currentStock > 0,
        convertedRequired: ingredient.amount,
      };
    }

    // ingredient.amount and currentStock are both in product.qu_id_stock units, so no conversion needed
    return {
      isAvailable: currentStock >= ingredient.amount,
      convertedRequired: ingredient.amount,
    };
  }

  // Find best conversion (product-specific first, then global)
  private findBestConversion(
    fromUnitId: number,
    toUnitId: number,
    productId: number,
  ): QuantityUnitConversion | null {
    // Try product-specific conversion first
    let conversion = this.conversions.find((c) =>
      c.from_qu_id === fromUnitId &&
      c.to_qu_id === toUnitId &&
      c.product_id === productId
    );

    // Fall back to global conversion
    if (!conversion) {
      conversion = this.conversions.find((c) =>
        c.from_qu_id === fromUnitId &&
        c.to_qu_id === toUnitId &&
        c.product_id === null
      );
    }

    // Try reverse conversion if direct not found
    if (!conversion) {
      conversion = this.conversions.find((c) =>
        c.from_qu_id === toUnitId &&
        c.to_qu_id === fromUnitId &&
        c.product_id === productId
      );

      if (!conversion) {
        conversion = this.conversions.find((c) =>
          c.from_qu_id === toUnitId &&
          c.to_qu_id === fromUnitId &&
          c.product_id === null
        );
      }

      if (conversion) {
        // Return a "reverse" conversion object
        return {
          ...conversion,
          from_qu_id: fromUnitId,
          to_qu_id: toUnitId,
          factor: 1 / conversion.factor,
        };
      }
    }

    return conversion || null;
  }

  // Find direct conversion (original logic)
  private findDirectConversion(
    amount: number,
    fromUnitId: number,
    toUnitId: number,
    productId: number,
  ): number | null {
    const conversion = this.findBestConversion(fromUnitId, toUnitId, productId);
    return conversion ? amount * conversion.factor : null;
  }

  // Find chained conversion through intermediate units
  private findChainedConversion(
    amount: number,
    fromUnitId: number,
    toUnitId: number,
    productId: number,
    visited: Set<number> = new Set(),
    depth: number = 0,
  ): number | null {
    // Prevent infinite loops and limit search depth
    if (visited.has(fromUnitId) || depth > 5) return null;
    visited.add(fromUnitId);

    // Get all possible conversions from current unit (product-specific first, then global)
    const productSpecificConversions = this.conversions.filter((c) =>
      c.from_qu_id === fromUnitId && c.product_id === productId
    );

    const globalConversions = this.conversions.filter((c) =>
      c.from_qu_id === fromUnitId && c.product_id === null
    );

    // Also try reverse conversions
    const productSpecificReverse = this.conversions.filter((c) =>
      c.to_qu_id === fromUnitId && c.product_id === productId
    ).map((c) => ({
      ...c,
      from_qu_id: c.to_qu_id,
      to_qu_id: c.from_qu_id,
      factor: 1 / c.factor,
    }));

    const globalReverse = this.conversions.filter((c) =>
      c.to_qu_id === fromUnitId && c.product_id === null
    ).map((c) => ({
      ...c,
      from_qu_id: c.to_qu_id,
      to_qu_id: c.from_qu_id,
      factor: 1 / c.factor,
    }));

    const allConversions = [
      ...productSpecificConversions,
      ...globalConversions,
      ...productSpecificReverse,
      ...globalReverse,
    ];

    for (const conversion of allConversions) {
      const intermediateAmount = amount * conversion.factor;

      if (conversion.to_qu_id === toUnitId) {
        // Found target unit directly
        return intermediateAmount;
      }

      // Try to convert from intermediate unit to target
      const finalAmount = this.findChainedConversion(
        intermediateAmount,
        conversion.to_qu_id,
        toUnitId,
        productId,
        new Set(visited), // Create new set to avoid modifying current path
        depth + 1,
      );

      if (finalAmount !== null) {
        return finalAmount;
      }
    }

    return null;
  }

  // Utility methods
  canConvert(fromUnitId: number, toUnitId: number, productId: number): boolean {
    return this.findBestConversion(fromUnitId, toUnitId, productId) !== null;
  }

  getConversionFactor(
    fromUnitId: number,
    toUnitId: number,
    productId: number,
  ): number | null {
    const conversion = this.findBestConversion(fromUnitId, toUnitId, productId);
    return conversion ? conversion.factor : null;
  }
}
